import React, { useEffect, useRef, useState } from 'react';
import { Mail, Check, CheckCheck, Eye, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  welcome: 'Welcome email',
  'email-confirmation': 'Confirmation link',
};

const STATUS_META = {
  sending: { text: 'Sending…', cls: 'bg-gray-100 text-gray-600', Icon: Loader2, spin: true },
  sent: { text: 'Sent', cls: 'bg-blue-100 text-blue-700', Icon: Check },
  delivered: { text: 'Delivered', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCheck },
  opened: { text: 'Opened', cls: 'bg-purple-100 text-purple-700', Icon: Eye },
  failed: { text: 'Delivery issue', cls: 'bg-red-100 text-red-700', Icon: AlertTriangle },
};

export const EmailBeacon = ({ email }) => {
  const [rows, setRows] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!email) return undefined;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/email-beacon?email=${encodeURIComponent(email)}`);
        if (r.ok) {
          const j = await r.json();
          if (stopped) return;
          setRows(j.emails || []);
          const done = (j.emails || []).length >= 2 &&
            j.emails.every((e) => ['delivered', 'opened', 'failed'].includes(e.status));
          if (done) return;
        }
      } catch (_e) { /* transient — keep polling */ }
      pollsRef.current += 1;
      if (!stopped && pollsRef.current < 20) timer = setTimeout(poll, 8000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [email]);

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="email-beacon">
      <div className="flex items-start gap-3">
        <Mail size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-900 mb-2">Your emails — live delivery status</p>
          {rows === null && (
            <p className="text-xs text-blue-700 flex items-center gap-1.5" data-testid="beacon-checking">
              <Loader2 size={12} className="animate-spin" /> Checking delivery…
            </p>
          )}
          {rows !== null && rows.length === 0 && (
            <p className="text-xs text-blue-700" data-testid="beacon-preparing">
              Your welcome email is being prepared — it usually lands within seconds.
            </p>
          )}
          {(rows || []).map((row) => {
            const meta = STATUS_META[row.status] || STATUS_META.sent;
            const { Icon } = meta;
            return (
              <div key={row.tag} className="flex items-center justify-between gap-2 py-1" data-testid={`beacon-row-${row.tag}`}>
                <span className="text-xs text-blue-800">{LABELS[row.tag] || row.tag}</span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.cls}`}
                  data-testid={`beacon-status-${row.tag}`}
                >
                  <Icon size={11} className={meta.spin ? 'animate-spin' : ''} /> {meta.text}
                </span>
              </div>
            );
          })}
          {(rows || []).some((r) => r.status === 'failed') && (
            <p className="text-[11px] text-red-700 mt-1.5" data-testid="beacon-failed-hint">
              One email couldn't be delivered — check your address or use the resend button below.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailBeacon;
